// src/components/Ownerdashboard/OwnerMediaModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Loader2, Trash2, UploadCloud, X } from "lucide-react";
import { toast } from "react-toastify";
import { ProbrokerLoadingOverlay } from "../ProbrokerLoader";
import {
  MAX_FILE_SIZE_MB,
  MAX_MEDIA_ITEMS,
  flattenImages,
  savePropertyImages,
  screenFiles,
  uploadPropertyImages,
} from "./ownerMedia";

let pickedId = 0;

/**
 * Media manager for a single owner property: shows the photos already on the
 * listing, lets them be removed, and stages newly picked files until "Save".
 * Nothing is written to the property until the owner confirms.
 */
const OwnerMediaModal = ({ property, onClose, onSaved }) => {
  const inputRef = useRef(null);
  const [existing, setExisting] = useState(() => flattenImages(property?.images));
  const [picked, setPicked] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState("");

  const savedImages = useMemo(() => flattenImages(property?.images), [property?.images]);
  const total = existing.length + picked.length;
  const slots = Math.max(MAX_MEDIA_ITEMS - total, 0);
  const removedCount = savedImages.length - existing.length;
  const dirty = picked.length > 0 || removedCount > 0;

  // Close on Escape and keep the page behind the modal from scrolling.
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === "Escape" && !busy) onClose?.();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKey);
    };
  }, [busy, onClose]);

  // Previews are object URLs, so they have to be handed back on unmount. The
  // ref keeps the cleanup pointing at the current list without re-running it.
  const pickedRef = useRef(picked);
  pickedRef.current = picked;

  useEffect(
    () => () => {
      pickedRef.current.forEach((item) => URL.revokeObjectURL(item.preview));
    },
    []
  );

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;

    const { accepted, tooLarge, wrongType, overflow } = screenFiles(files, slots);

    if (wrongType.length > 0) {
      toast.error(
        wrongType.length === 1
          ? `${wrongType[0]} is not an image.`
          : `${wrongType.length} files were skipped - images only.`
      );
    }

    if (tooLarge.length > 0) {
      toast.error(
        tooLarge.length === 1
          ? `${tooLarge[0]} is over ${MAX_FILE_SIZE_MB}MB.`
          : `${tooLarge.length} photos are over ${MAX_FILE_SIZE_MB}MB.`
      );
    }

    if (overflow > 0) {
      toast.warn(`Only ${MAX_MEDIA_ITEMS} photos are allowed per property.`);
    }

    if (accepted.length === 0) return;

    setPicked((prev) => [
      ...prev,
      ...accepted.map((file) => ({
        id: (pickedId += 1),
        file,
        preview: URL.createObjectURL(file),
      })),
    ]);
  };

  const handleInputChange = (event) => {
    addFiles(event.target.files);
    // Lets the same file be picked again after it was removed.
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (!busy) addFiles(event.dataTransfer?.files);
  };

  const removePicked = (id) => {
    setPicked((prev) => {
      const target = prev.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((item) => item.id !== id);
    });
  };

  const removeExisting = (url) => {
    setExisting((prev) => prev.filter((item) => item !== url));
  };

  const handleSave = async () => {
    if (!dirty || busy) return;

    setBusy(true);
    setBusyMessage(picked.length > 0 ? "Uploading your photos" : "Saving your changes");

    let uploaded = [];

    if (picked.length > 0) {
      const upload = await uploadPropertyImages(
        picked.map((item) => item.file),
        (current, total) =>
          setBusyMessage(
            total === 1 ? "Uploading your photo" : `Uploading photo ${current} of ${total}`
          )
      );

      if (!upload.success) {
        setBusy(false);
        toast.error(upload.message);
        return;
      }

      uploaded = upload.urls;
    }

    setBusyMessage("Saving your changes");

    const saved = await savePropertyImages({
      id: property?._id,
      // One URL per entry - the backend stores them as separate images.
      images: [...existing, ...uploaded],
    });

    setBusy(false);

    if (!saved.success) {
      toast.error(saved.message);
      return;
    }

    const parts = [];
    if (uploaded.length > 0) {
      parts.push(uploaded.length === 1 ? "1 photo added" : `${uploaded.length} photos added`);
    }
    if (removedCount > 0) {
      parts.push(removedCount === 1 ? "1 removed" : `${removedCount} removed`);
    }

    toast.success(`${parts.join(", ")}.`);
    picked.forEach((item) => URL.revokeObjectURL(item.preview));
    onSaved?.();
    onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4"
      onClick={() => !busy && onClose?.()}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EFE9FF] text-[#503691]">
              <ImagePlus className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-base font-bold text-gray-900 sm:text-lg">Add Media</h2>
              <p className="text-xs text-gray-500 sm:text-sm">
                Up to {MAX_MEDIA_ITEMS} photos, {MAX_FILE_SIZE_MB}MB each
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => !busy && onClose?.()}
            aria-label="Close"
            className="rounded-full p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {/* Usage meter */}
          <div className="mb-4">
            <div className="mb-1.5 flex items-center justify-between text-xs font-medium sm:text-sm">
              <span className="text-gray-600">
                {total} of {MAX_MEDIA_ITEMS} used
              </span>
              <span className={slots === 0 ? "text-red-500" : "text-gray-400"}>
                {slots === 0 ? "Limit reached" : `${slots} left`}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
              <div
                className={`h-full rounded-full transition-all duration-300 ${
                  slots === 0 ? "bg-red-400" : "bg-[#503691]"
                }`}
                style={{ width: `${(total / MAX_MEDIA_ITEMS) * 100}%` }}
              />
            </div>
          </div>

          {/* Picker */}
          <div
            onDragOver={(event) => {
              event.preventDefault();
              if (!busy && slots > 0) setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
            className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
              slots === 0
                ? "border-gray-200 bg-gray-50"
                : dragging
                  ? "border-[#503691] bg-[#EFE9FF]"
                  : "border-gray-300 bg-gray-50 hover:border-[#503691]"
            }`}
          >
            <UploadCloud
              className={`mb-2 h-8 w-8 ${slots === 0 ? "text-gray-300" : "text-[#503691]"}`}
            />

            {slots === 0 ? (
              <p className="text-sm text-gray-500">
                You have reached the {MAX_MEDIA_ITEMS} photo limit. Remove one to add another.
              </p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  disabled={busy}
                  className="rounded-full bg-[#503691] px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#5b54e6] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  Select File
                </button>
                <p className="mt-2 text-xs text-gray-500">
                  or drag and drop here &middot; JPG, PNG up to {MAX_FILE_SIZE_MB}MB
                </p>
              </>
            )}
          </div>

          {/* Selected media */}
          {total > 0 ? (
            <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {existing.map((url) => (
                <MediaTile
                  key={url}
                  src={url}
                  disabled={busy}
                  onRemove={() => removeExisting(url)}
                />
              ))}

              {picked.map((item) => (
                <MediaTile
                  key={item.id}
                  src={item.preview}
                  badge="New"
                  disabled={busy}
                  onRemove={() => removePicked(item.id)}
                />
              ))}
            </div>
          ) : (
            <p className="mt-5 text-center text-sm text-gray-500">
              No photos on this property yet.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-4 py-3 sm:px-6">
          <p className="hidden text-xs text-gray-500 sm:block">
            {dirty ? "Unsaved changes" : "Nothing to save yet"}
          </p>

          <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
            <button
              type="button"
              onClick={() => !busy && onClose?.()}
              disabled={busy}
              className="flex-1 rounded-full border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={!dirty || busy}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#503691] px-6 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#5b54e6] disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy ? "Saving..." : "Save"}
            </button>
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={handleInputChange}
        />
      </div>

      {/* Branded upload popup, sits above the modal while work is running */}
      {busy && <ProbrokerLoadingOverlay message={busyMessage || "Working on it"} />}
    </div>
  );
};

const MediaTile = ({ src, badge, disabled, onRemove }) => (
  <div className="group relative aspect-square overflow-hidden rounded-xl border border-gray-200 bg-gray-100">
    <img src={src} alt="" loading="lazy" className="h-full w-full object-cover" />

    {badge && (
      <span className="absolute bottom-1.5 left-1.5 rounded-full bg-[#503691] px-2 py-0.5 text-[10px] font-semibold text-white">
        {badge}
      </span>
    )}

    <button
      type="button"
      onClick={onRemove}
      disabled={disabled}
      aria-label="Remove photo"
      className="absolute right-1.5 top-1.5 rounded-full bg-black/60 p-1.5 text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  </div>
);

export default OwnerMediaModal;
